/**
 * Probability helpers aligned with backend `IProbabilityUtil` (subset used by slot pot, etc.).
 */
export interface IProbUtil {
  isWin(winningProbability: number): boolean
  drawFromWeightMap(weightMap: Readonly<Record<number, number>>): number
}

/** Uniform integer in [0, 10000), matching `GenerateResult(0, 10000)` style draws. */
function randomTenThousand(random: () => number): number {
  return Math.floor(random() * 10000)
}

export function createProbUtil(random: () => number = Math.random): IProbUtil {
  return {
    isWin(winningProbability: number): boolean {
      if (winningProbability < 0 || winningProbability > 1) {
        throw new RangeError('winningProbability must be between 0 and 1')
      }
      if (winningProbability === 0)
        return false

      const threshold = winningProbability * 10000
      const value = randomTenThousand(random)
      return value < threshold
    },

    drawFromWeightMap(weightMap: Readonly<Record<number, number>>): number {
      const map = new Map<number, number>(
        Object.entries(weightMap).map(([k, w]) => [Number(k), w]),
      )
      return drawFromWeightMap(map)
    },
  }
}

/** Default implementation using `Math.random`. */
export const probUtil: IProbUtil = createProbUtil()

export function drawFromWeightMap<T extends string | number | symbol | boolean>(weightMap: Map<T, number>): T {
  const probBase = Array.from(weightMap.values()).reduce((sum, weight) => sum + weight, 0)
  const prob = Math.floor(Math.random() * probBase)

  let cumulativeWeight = 0
  for (const [symbol, weight] of weightMap) {
    cumulativeWeight += weight
    if (prob < cumulativeWeight) {
      return symbol
    }
  }

  throw new Error('Invalid weight map')
}

export const getRandomIntBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min
